import { Component, EventEmitter, Input, Output, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  getLayoutEditorColSpanLabel,
  getUnifiedDashboardTileColClass,
  UNIFIED_DASHBOARD_GRID_CLASS,
} from '../../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '../../../../../shared/components/confirmation-modal/confirmation-modal.component';
export interface CustomizeDashboardPreviewTile {
  id: string;
  title: string;
  /** Short source-page label chip (e.g. MR1, SEO, Training). */
  source: string;
  /** 4 | 6 | 8 | 12 inside a 12-col grid. */
  colSpan: 4 | 6 | 8 | 12;

  visible?: boolean;

  /**
   * Backend widget_key (or canonical card id) the tile represents. Used by
   * the popup to detect cards that should render as TALL tiles
   * (row-span-2 at col-span 8) so the popup preview mirrors what the live
   * My Dashboard grid will render — e.g. "Top Categories by Contracts" with
   * two col-span-4 cards stacked on its right side. Optional so legacy
   * callers that pass plain `{ id, title, source, colSpan }` keep working
   * unchanged.
   */
  widgetKey?: string;
}

/**
 * Canonical / alias widget keys for cards that should render as TALL
 * tiles (row-span-2) in the popup preview when their col-span is 8.
 * Mirrors `TALL_TILE_WIDGET_KEYS_AT_COLSPAN_8` in my-dashboard.component.ts
 * and the `lg:row-span-2` hint MR1 emits on its standalone page.
 */
const TALL_PREVIEW_WIDGET_KEYS_AT_COLSPAN_8: ReadonlySet<string> = new Set([
  'chart_top_categories_contracts',
  'mr1_top_categories_contracts',
]);

/** Strip product-prefix (e.g. `de_mr1_…` → `mr1_…`) the same way the shared catalog helper does. */
function stripPreviewWidgetKeyPrefix(widgetKey: string): string {
  const match = /^[a-z]{2}_(.+)$/i.exec(widgetKey);
  return match ? match[1] : widgetKey;
}

/** Allowed col-span values for a preview tile. */
export type CustomizeDashboardColSpan = 4 | 6 | 8 | 12;

/** Normalize an arbitrary backend col_span into the 4 / 6 / 8 / 12 steps the preview uses. */
export function normalizeCustomizeColSpan(span: number | null | undefined): CustomizeDashboardColSpan {
  if (span == null || !Number.isFinite(span)) return 12;
  if (span <= 4) return 4;
  if (span <= 6) return 6;
  if (span <= 8) return 8;
  return 12;
}

/** Allowed col-span steps the user can cycle through per card. */
const COL_SPAN_STEPS: ReadonlyArray<4 | 6 | 8 | 12> = [4, 6, 8, 12];

export interface CustomizeDashboardStateApplyResult {
  tiles: CustomizeDashboardPreviewTile[];
  mixed: boolean;
}
export type CustomizeDashboardStateResult =
  | CustomizeDashboardStateApplyResult
  | CustomizeDashboardPreviewTile[]
  | null;

@Component({
  selector: 'app-customize-dashboard-state',
  standalone: true,
  imports: [CommonModule, DragDropModule, IconComponentV2, MatDialogModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './customize-dashboard-state.component.html',
})
export class CustomizeDashboardStateComponent {
  private readonly dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<CustomizeDashboardStateComponent, CustomizeDashboardStateResult>);

  /** Same 12-col grid classes as `my-dashboard.component.html`. */
  readonly unifiedGridClass = UNIFIED_DASHBOARD_GRID_CLASS;

  @Input() title = 'Build your dashboard';
  @Input() description =
    'Preview a combined Monthly Report 1 + Monthly Report 2 layout below, then add sections to get started.';
  @Input() buttonLabel = 'Apply';


  @Input() set apiTiles(value: CustomizeDashboardPreviewTile[] | null | undefined) {
    const next = Array.isArray(value) && value.length > 0
      ? value.map((t) => ({ ...t, visible: t.visible !== false }))
      : null;
    this._apiTiles.set(next);
    this.previewTiles.set(this.computeInitialTiles());
  }

  /** Live snapshot of the parent-supplied tiles (null when none provided). */
  private readonly _apiTiles = signal<CustomizeDashboardPreviewTile[] | null>(null);

  @Input() set defaultTiles(value: CustomizeDashboardPreviewTile[] | null | undefined) {
    const next =
      Array.isArray(value) && value.length > 0
        ? value.map((t) => ({
          ...t,
          colSpan: normalizeCustomizeColSpan(t.colSpan),
          visible: t.visible !== false,
        }))
        : null;
    this._defaultTiles.set(next);
  }

  /** Live snapshot of the parent-supplied defaults (null when none provided). */
  private readonly _defaultTiles = signal<CustomizeDashboardPreviewTile[] | null>(null);
  @Output() apply = new EventEmitter<CustomizeDashboardPreviewTile[]>();
  @Output() addFirstSection = new EventEmitter<void>();
  /** Emitted when the inner component needs the parent dialog to run its Apply
   *  flow (e.g. after a tile is deleted via the confirmation modal). */
  @Output() applyRequested = new EventEmitter<void>();

  private readonly fallbackTiles: CustomizeDashboardPreviewTile[] = [
    // ── Monthly Report 2 — high-level KPI strip first
    { id: 'mr2_stats_overview', title: 'Stats Overview', source: 'CI', colSpan: 12 },

    // ── Monthly Report 1 — primary analytics row
    { id: 'mr1_active_businesses', title: 'MoM Active Businesses', source: 'MO', colSpan: 8 },
    { id: 'mr1_contract_types', title: 'Contract Types Overview', source: 'MO', colSpan: 4 },

    // ── Monthly Report 2 — running totals
    { id: 'mr2_enrolled_running', title: 'Businesses Enrolled | Running Total', source: 'CI', colSpan: 6 },
    { id: 'mr2_contracts_running', title: 'Contracts Created | Running Total', source: 'CI', colSpan: 6 },

    // ── Monthly Report 1 — distribution + top items
    { id: 'mr1_top_categories', title: 'Top Categories by Contracts', source: 'MO', colSpan: 8 },
    { id: 'mr1_overall_pie', title: 'Overall Contract Type Count', source: 'MO', colSpan: 4 },

    // ── Monthly Report 1 — geo lists
    { id: 'mr1_enrolled', title: 'Enrolled Businesses', source: 'MO', colSpan: 6 },
    { id: 'mr1_contracts_created', title: 'Contracts Created', source: 'MO', colSpan: 6 },

    // ── Monthly Report 2 — activity + status
    { id: 'mr2_mom_activity', title: 'MoM Activity Chart', source: 'CI', colSpan: 8 },
    { id: 'mr2_training_status', title: 'Training Status', source: 'CI', colSpan: 4 },

    // ── Monthly Report 1 — bottom rich tiles
    { id: 'mr1_top_10_businesses', title: 'Top 10 Businesses by Contracts', source: 'MO', colSpan: 12 },
    { id: 'mr2_monthly_analysis', title: 'Monthly Activity Analysis', source: 'CI', colSpan: 12 },
    { id: 'mr1_business_table', title: 'Businesses wise Contract / Overall Detail', source: 'MO', colSpan: 12 },
  ];


  readonly previewTiles = signal<CustomizeDashboardPreviewTile[]>(
    this.fallbackTiles.map((t) => ({ ...t, visible: t.visible !== false })),
  );

  private computeInitialTiles(): CustomizeDashboardPreviewTile[] {
    const api = this._apiTiles();
    const source = api && api.length > 0 ? api : this.fallbackTiles;
    return source.map((t) => ({ ...t, visible: t.visible !== false }));
  }

  /** Map a tile colSpan to the same Tailwind classes as the live My Dashboard grid. */
  getPreviewColSpanClass(span: number): string {
    return getUnifiedDashboardTileColClass(span);
  }

  /**
   * Returns `true` when the tile represents a card that should render as a
   * TALL tile (row-span-2) at col-span 8 in the popup preview. Only the
   * "Top Categories by Contracts" widget opts in today; every other tile
   * keeps its existing single-row behaviour so this change cannot regress
   * unrelated layouts.
   */
  isTallPreviewTile(tile: CustomizeDashboardPreviewTile): boolean {
    const key = tile.widgetKey ? stripPreviewWidgetKeyPrefix(tile.widgetKey) : stripPreviewWidgetKeyPrefix(tile.id);
    const originalKey = tile.widgetKey || tile.id;

    if (tile.colSpan === 8) {
      if (originalKey && TALL_PREVIEW_WIDGET_KEYS_AT_COLSPAN_8.has(originalKey)) return true;
      if (TALL_PREVIEW_WIDGET_KEYS_AT_COLSPAN_8.has(key)) return true;
    }

    if (key === 'mr2_training_status' && tile.colSpan === 4) {
      const tiles = this.previewTiles();
      const stats = tiles.find(t => {
        const k = t.widgetKey ? stripPreviewWidgetKeyPrefix(t.widgetKey) : stripPreviewWidgetKeyPrefix(t.id);
        return k === 'mr2_stats_overview';
      });
      const mom = tiles.find(t => {
        const k = t.widgetKey ? stripPreviewWidgetKeyPrefix(t.widgetKey) : stripPreviewWidgetKeyPrefix(t.id);
        return k === 'mr2_mom_activity';
      });
      if (stats && mom && stats.visible !== false && mom.visible !== false && stats.colSpan > 4 && mom.colSpan > 4) {
        return true;
      }
    }

    if ((key === 'ga_cbc_fetch_outcomes' || key === 'gar_cbc_effect') && tile.colSpan === 8) {
      const tiles = this.previewTiles();
      const hasPayment = tiles.some(t => {
        const tId = t.widgetKey ? stripPreviewWidgetKeyPrefix(t.widgetKey) : stripPreviewWidgetKeyPrefix(t.id);
        return tId === 'ga_payment_method_distribution' || tId === 'gar_payment_method';
      });
      const hasSimulator = tiles.some(t => {
        const tId = t.widgetKey ? stripPreviewWidgetKeyPrefix(t.widgetKey) : stripPreviewWidgetKeyPrefix(t.id);
        return tId === 'ga_simulator_usage_distribution' || tId === 'gar_simulator_usage';
      });
      if (hasPayment && hasSimulator) {
        return true;
      }
    }

    return false;
  }

  /**
   * Combined col-span (+ optional row-span-2) Tailwind class for a preview
   * tile — the popup mirrors the live My Dashboard packing exactly so the
   * user sees the same arrangement they're about to apply.
   */
  getPreviewTileClass(tile: CustomizeDashboardPreviewTile): string {
    const colClass = this.getPreviewColSpanClass(tile.colSpan);
    return this.isTallPreviewTile(tile) ? `${colClass} lg:row-span-2` : colClass;
  }

  /** Human-readable width label (matches MR1's customize layout pill copy). */
  getColSpanLabel(span: number): string {
    return getLayoutEditorColSpanLabel(span);
  }

  /**
   * Look up the **designed default** col-span for a tile (sourced from the
   * parent-supplied `defaultTiles` input). Returns `null` when no default is
   * registered for the given id — in that case the stepper is unrestricted.
   */
  private getDefaultColSpan(id: string): CustomizeDashboardColSpan | null {
    const defaults = this._defaultTiles();
    if (!defaults) return null;
    const found = defaults.find((d) => d.id === id);
    return found ? found.colSpan : null;
  }

  /**
   * Whether the minimize button should be active for this tile.
   *
   * A card cannot shrink below its **designed default** col-span — e.g. a
   * card designed for `colSpan = 8` can only step between 8 and 12, never
   * down to 6 or 4. The button is disabled the moment the card reaches its
   * default width.
   */
  canDecreaseColSpan(tile: CustomizeDashboardPreviewTile): boolean {
    const idx = COL_SPAN_STEPS.indexOf(tile.colSpan);
    if (idx <= 0) return false;
    const defaultColSpan = this.getDefaultColSpan(tile.id);
    if (defaultColSpan != null && COL_SPAN_STEPS[idx - 1] < defaultColSpan) {
      return false;
    }
    return true;
  }

  /** Whether the maximize button should be active for this tile. */
  canIncreaseColSpan(tile: CustomizeDashboardPreviewTile): boolean {
    const idx = COL_SPAN_STEPS.indexOf(tile.colSpan);
    return idx >= 0 && idx < COL_SPAN_STEPS.length - 1;
  }
  //  eye icon for hides/shows widget on the main dashboard
  toggleEditorCardVisibility(id: string): void {
    this.previewTiles.update((items) =>
      this._reorderTilesOnVisibilityToggle(items, id),
    );
  }

  private _reorderTilesOnVisibilityToggle(
    items: CustomizeDashboardPreviewTile[],
    id: string,
  ): CustomizeDashboardPreviewTile[] {
    const idx = items.findIndex((i) => i.id === id);
    if (idx === -1) return items;

    const current = items[idx];
    const becomingHidden = current.visible !== false;
    const updated: CustomizeDashboardPreviewTile = {
      ...current,
      visible: !becomingHidden,
    };

    const without = items.filter((_, i) => i !== idx);

    if (becomingHidden) {
      return [...without, updated];
    }

    const firstHiddenIdx = without.findIndex((i) => i.visible === false);
    if (firstHiddenIdx === -1) {
      return [...without, updated];
    }
    return [
      ...without.slice(0, firstHiddenIdx),
      updated,
      ...without.slice(firstHiddenIdx),
    ];
  }

  /** Count of currently-visible preview tiles (used by the header chip). */
  get editorVisibleCount(): number {
    return this.previewTiles().filter((t) => t.visible !== false).length;
  }

  /** Steps a card down one width (e.g. 12 → 8 → 6 → 4). Respects each tile's designed default as the lower bound. */
  decreaseColSpan(id: string): void {
    this.previewTiles.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = COL_SPAN_STEPS.indexOf(i.colSpan);
        if (idx <= 0) return i;
        const next = COL_SPAN_STEPS[idx - 1];
        const defaultColSpan = this.getDefaultColSpan(i.id);
        if (defaultColSpan != null && next < defaultColSpan) return i;
        return { ...i, colSpan: next };
      }),
    );
  }

  /** Steps a card up one width (e.g. 4 → 6 → 8 → 12). */
  increaseColSpan(id: string): void {
    this.previewTiles.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = COL_SPAN_STEPS.indexOf(i.colSpan);
        if (idx < 0 || idx >= COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: COL_SPAN_STEPS[idx + 1] };
      }),
    );
  }

  /**
   * Auto-adjusts widths in the preview so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest tile in each leaky row (and shares the
   * leftover equally when multiple tiles tie at the smallest width). The
   * apply step is kept inline so the literal `CustomizeDashboardColSpan`
   * (`4 | 6 | 8 | 12`) type is preserved on each updated tile — the shared
   * apply helper would widen it to `number`. The shared helper's dense-pack
   * simulation also matches the live `my-dashboard` grid's
   * `grid-flow-row-dense` layout, so the preview stays in sync with the
   * actual render.
   */
  autoAdjustWidths(): void {
    const isTall = (t: any) =>
      t.widgetKey && t.colSpan === 8
        ? TALL_PREVIEW_WIDGET_KEYS_AT_COLSPAN_8.has(t.widgetKey) ||
        TALL_PREVIEW_WIDGET_KEYS_AT_COLSPAN_8.has(stripPreviewWidgetKeyPrefix(t.widgetKey))
        : false;

    const updates = computeLayoutEditorAutoWidthUpdates(
      this._normalizedTilesForAutoWidth(),
      isTall
    );
    if (!updates.size) return;

    this.previewTiles.update((items) =>
      items.map((i) =>
        updates.has(i.id)
          ? { ...i, colSpan: updates.get(i.id) as CustomizeDashboardColSpan }
          : i,
      ),
    );
  }

  /** Whether clicking Auto Adjust Width would actually grow at least one tile. */
  canAutoAdjustWidths(): boolean {
    const isTall = (t: any) =>
      t.widgetKey && t.colSpan === 8
        ? TALL_PREVIEW_WIDGET_KEYS_AT_COLSPAN_8.has(t.widgetKey) ||
        TALL_PREVIEW_WIDGET_KEYS_AT_COLSPAN_8.has(stripPreviewWidgetKeyPrefix(t.widgetKey))
        : false;

    return computeLayoutEditorAutoWidthUpdates(
      this._normalizedTilesForAutoWidth(),
      isTall
    ).size > 0;
  }

  /**
   * Normalize the preview tiles for the shared auto-width helper:
   * `CustomizeDashboardPreviewTile.visible` is optional (undefined means
   * visible, matching `t.visible !== false`), but the shared helper requires
   * `visible: boolean`.
   */
  private _normalizedTilesForAutoWidth(): Array<{
    id: string;
    visible: boolean;
    colSpan: number;
    widgetKey?: string;
  }> {
    return this.previewTiles().map((t) => ({
      id: t.id,
      visible: t.visible !== false,
      colSpan: t.colSpan,
      widgetKey: t.widgetKey,
    }));
  }

  /**
   * Restore every tile back to its default arrangement — BOTH width AND order. 
   */
  resetWidths(): void {
    const defaults = this._defaultTiles();
    if (defaults && defaults.length > 0) {
      const defaultIds = new Set(defaults.map((t) => t.id));
      const extras = this.previewTiles().filter((t) => !defaultIds.has(t.id));
      const next = [
        ...defaults.map((t) => ({ ...t, visible: t.visible !== false })),
        ...extras.map((t) => ({ ...t, visible: t.visible !== false })),
      ];
      this.previewTiles.set(next);
      return;
    }
    this.previewTiles.set(this.computeInitialTiles());
  }

  /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onTileDrop(event: CdkDragDrop<CustomizeDashboardPreviewTile[]>): void {
    const next = applyLayoutEditorCardDrop(this.previewTiles(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.previewTiles.set(next);
  }

  /** Track-by for the preview grid — keeps DOM nodes stable when widths change. */
  trackTile = (_idx: number, tile: CustomizeDashboardPreviewTile): string => tile.id;

  /** Static count of MR1 / MR2 sections shown in the preview. */
  get mr1Count(): number {
    return this.previewTiles().filter((t) => t.source === 'MO').length;
  }
  get mr2Count(): number {
    return this.previewTiles().filter((t) => t.source === 'CI').length;
  }

  onAddFirstSection(): void {
    this.addFirstSection.emit();
  }
  onApply(): void {
    this.apply.emit(this.previewTiles().map((t) => ({ ...t })));
    this.addFirstSection.emit();
  }
  // delete single card from custom dashboard — confirm first, then remove
  deleteTile(id: string): void {
    const tile = this.previewTiles().find((i) => i.id === id);
    if (!tile) return;

    const safeTitle = this._escapeHtml(tile.title);
    const ref = this.dialog.open<
      ConfirmationModalComponent,
      ConfirmationModalData,
      boolean
    >(ConfirmationModalComponent, {
      width: '400px',
      data: {
        title: 'Delete Widget?',
        description: `Are you sure you want to delete "${tile.title}" from your custom dashboard?`,
        descriptionHtml: `Are you sure you want to delete "<strong>${safeTitle}</strong>" from your custom dashboard?`,
        confirmLabel: 'Yes',
        cancelLabel: 'No',
      },
    });

    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed !== true) return;
      this.previewTiles.update((items) => items.filter((i) => i.id !== id));
      this.applyRequested.emit();
    });

  }

  /** Escape HTML-special chars so dynamic text stays safe inside `descriptionHtml`. */
  private _escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

}
